import { type Components } from '@mui/material'
import type { Theme, Tokens } from '../augmentation'
import { spacing } from '../spacing'

export const createMuiDataGrid = ({ palette, typography }: Pick<Tokens, 'palette' | 'typography'>): Components<Theme> =>
  ({
    MuiDataGrid: {
      styleOverrides: {
        toolbarContainer: {
          padding: spacing(0, 3),
        },
        columnHeader: {
          ...typography.body2,
          color: palette.text.tertiary,
          padding: spacing(0, 1),

          background: palette.pure.light,
        },
        footerContainer: {
          padding: spacing(2, 3),
          background: palette.pure.light,
          borderColor: palette.components.divider.line,
          whiteSpace: 'nowrap',
        },
        selectedRowCount: {
          visibility: 'visible',
          width: 'auto',
          minWidth: 'max-content',
          marginLeft: 0,
          marginRight: spacing(1),
          whiteSpace: 'nowrap',
        },
        virtualScrollerRenderZone: {
          '& .MuiDataGrid-cell[role="gridcell"]': {
            ...typography.body1,
            color: palette.grey[700],
            borderBottom: 'none',
            padding: spacing(0, 1),
            display: 'flex',
            alignItems: 'center',

            '&:focus': {
              outline: 'none',
            },
          },

          '& .MuiDataGrid-cell[role="gridcell"][aria-colindex="1"]:not(.MuiDataGrid-cellCheckbox)': {
            paddingLeft: spacing(3),
          },

          '& .MuiDataGrid-cellCheckbox + .MuiDataGrid-cell': {
            paddingLeft: spacing(3),
          },

          ['& .MuiDataGrid-cell[role="gridcell"]:has(+ .MuiDataGrid-cellEmpty), ' +
          '& .MuiDataGrid-cell[role="gridcell"]:last-child']: {
            paddingRight: spacing(3),
          },
        },
        columnHeaders: {
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },

          ['& .MuiDataGrid-columnHeader[role="columnheader"][aria-colindex="1"]' +
          ':not(.MuiDataGrid-columnHeaderCheckbox)']: {
            paddingLeft: spacing(3),
          },

          '& .MuiDataGrid-columnHeaderCheckbox + .MuiDataGrid-columnHeader': {
            paddingLeft: spacing(3),
          },

          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent': {
            overflow: 'visible',
          },

          '& .MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--last': {
            paddingRight: spacing(3),
          },
        },
        root: {
          border: 'none',
          borderRadius: 0,

          '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within, .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },

          '& .MuiDataGrid-main': {
            // start fix: https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
            width: 0,
            minWidth: '100%',
            // end fix
            backgroundColor: palette.pure.light,
            border: 'none',
            borderRadius: 'unset',
          },

          '&.DhlTable--plain .MuiDataGrid-row': {
            borderBottom: `1px solid ${palette.grey[250]}`,

            '&:nth-of-type(odd)': {
              backgroundColor: `${palette.background.paper} !important`,
            },
          },

          '&:not(.DhlTable--plain) .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: palette.grey[200],
          },

          '& .MuiDataGrid-row': {
            '&:nth-of-type(odd).Mui-selected , &.Mui-selected': {
              backgroundColor: palette.components.dataGrid.hover,
            },
          },

          '& .MuiDataGrid-row-disabled .MuiDataGrid-cell': {
            color: palette.text.disabled,
          },

          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `2px solid ${palette.components.divider.line}`,
            borderRadius: 0,
          },

          '& [role=row].MuiDataGrid-row--borderBottom': {
            backgroundColor: palette.pure.light,
          },

          '& .MuiDataGrid-columnHeaderWrapper': {
            paddingLeft: 6,
          },

          '& .MuiDataGrid-columnHeaderTitle:focus': {
            outline: 'none',
          },

          '& .MuiDataGrid-columnsContainer': {
            borderBottom: 'none',
            justifyContent: 'center',
          },
        },
      },
    },

    // reason: here we do not have injection of x-data-grid
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any
